<template>
  <div>
    <template v-if="loading">
      <v-progress-circular indeterminate ></v-progress-circular>
    </template>
    <template v-else>
      <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle>{{ item.identityNo }}</v-list-item-subtitle>
            <v-list-item-title>모뎀번호</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      <v-subheader>Hex</v-subheader>
      <v-container fluid>
        <div>
          <span v-for="(hex, index) in hexChunks" :key="index" :class="['mr-1 px-1', `hexblock-${index % 6 + 1}`, selectedClass(index, selected)]">{{ hex }}</span>
        </div>
      </v-container>
      <v-container fluid>
        <v-row>
          <template v-for="({ label, value, filter, visible, suffix }, index) in parsed">
            <v-col cols="auto" :key="label" v-if="visible">
              <v-card @click="select(index)">
                <v-card-title class="pa-2"><h6>{{ label }}</h6></v-card-title>
                <!-- <template v-if="label === 'Temp' || label === 'Analog'">
                  <v-card-text>
                    <v-row>
                      <v-col cols="auto" v-for="(int, index) in filter(value)" :key="index">
                        <span class="mr-1 font-weight-bold">{{ index + 1 }}:</span>{{ int }}<span class="ml-1">C</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </template> -->
                <template>
                  <v-card-text class="pb-2 px-2">
                    {{ filter(value) }}
                    <span v-if="suffix" class="ml-1">{{ suffix }}</span>
                  </v-card-text>
                </template>
              </v-card>
            </v-col>
          </template>
        </v-row>
      </v-container>
    </template>
  </div>
</template>

<script>
import { getLastPacket } from '@/api'
import moment from 'moment'

class PacketParser {
  constructor (
    label,
    length,
    options = {}
  ) {
    this.label = label
    this.unsigned = options.unsigned || false
    this.visible = options.visible === null || options.visible === undefined ? true : options.visible
    this.filter = options.filter ? options.filter : val => val
    this.suffix = options.suffix || ''
    this.length = length
  }

  method (offset) {
    switch (this.length) {
      case 1:
        return (view) => this.unsigned ? view.getUint8(offset) : view.getInt8(offset)
      case 2:
        return (view) => this.unsigned ? view.getUint16(offset, true) : view.getInt16(offset, true)
      case 4:
        return (view) => this.unsigned ? view.getUint32(offset, true) : view.getInt32(offset, true)
      default:
        return (view) => new DataView(view.buffer.slice(offset, offset + this.length))
    }
  }
}

// function toHexString (arr) {
//   return arr.map((int) => int.toString(16).padStart(2, '0').toUpperCase()).join(' ')
// }

const parsers = [
  new PacketParser('Time', 4, { unsigned: true, filter: (timestamp) => moment(new Date(timestamp * 1000)).add(-9, 'hour').format('YYYY-MM-DD HH:mm:ss') }),
  new PacketParser('Signal', 2, { unsigned: true, visible: false }),
  new PacketParser('Status', 2, { unsigned: true, visible: false }),
  new PacketParser('Latitude', 4, { filter: (lat) => (lat * 0.000001).toFixed(6) }),
  new PacketParser('Longitude', 4, { filter: (lng) => (lng * 0.000001).toFixed(6) }),
  new PacketParser('Azimuth', 2, {}),
  new PacketParser('RPM', 2, { unsigned: true }),
  new PacketParser('Speed', 1, { unsigned: true, suffix: 'km/h' }),
  new PacketParser('TripCount', 1, { unsigned: true, visible: false }),
  new PacketParser('Tenable', 2, { unsigned: true, visible: false }),
  new PacketParser('Aenable', 1, { unsigned: true, visible: false }),
  new PacketParser('Denable', 1, { unsigned: true, visible: false }),
  ...new Array(16).fill(0).map((val, index) => {
    return new PacketParser('Temp' + (index + 1), 2, {
      suffix: 'C'
    })
  }),
  ...new Array(8).fill(0).map((val, index) => {
    return new PacketParser('Analog' + (index + 1), 2, {
      filter: val => (val * 0.01).toFixed(2),
      suffix: 'V'
    })
  }),
  ...new Array(4).fill(0).map((val, index) => {
    return new PacketParser('D_Input' + (index + 1), 1, {})
  }),
  new PacketParser('D_Output', 1 * 2, { visible: false }),
  new PacketParser('StackVolt', 2, { unsigned: true, suffix: 'V', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('StackCurr', 2, { unsigned: true, suffix: 'A', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('DcDcVolt', 2, { unsigned: true, suffix: 'V', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('DcDcCurr', 2, { unsigned: true, suffix: 'A', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('BMS_Volt', 2, { unsigned: true, suffix: 'V', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('BMS_ChCurr', 2, { unsigned: true, suffix: 'A', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('BMS_DcHcCurr', 2, { unsigned: true, suffix: 'A', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('BMS_Soc', 1, { unsigned: true, suffix: '%' }),
  new PacketParser('BMS_Status', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('PTDA1', 2, { unsigned: true, suffix: 'bar' }),
  new PacketParser('PTDA2', 1, { unsigned: true, suffix: 'bar' }),
  new PacketParser('PTDA3', 1, { unsigned: true, suffix: 'bar' }),
  new PacketParser('MFM_Data', 1, { unsigned: true, suffix: 'LPM' }),
  new PacketParser('Thermo', 1, { unsigned: true, suffix: 'us/cm^2', filter: val => (val * 0.1).toFixed(1) }),
  new PacketParser('Hydrogen', 2, { unsigned: true, suffix: 'ppm', filter: val => (val * 0.0001).toFixed(4) }),
  new PacketParser('TempDa0', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('TempDa1', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('TempDa2', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('TempDa3', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('TempDa4', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('TempDa5', 2, { unsigned: true, suffix: 'C', filter: val => (val * 0.01).toFixed(2) }),
  new PacketParser('Blower', 2, { unsigned: true, suffix: 'LPM' }),
  new PacketParser('BlowerSet', 2, { unsigned: true, suffix: 'LPM' }),
  new PacketParser('StackSta', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('Control', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('RunMode', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('MessageCode', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('CoolingFan', 2, { unsigned: true, suffix: 'RPM' }),
  new PacketParser('LevelSensor', 1, { unsigned: true, filter: val => '0x' + val.toString(16).padStart(2, '0').toUpperCase() }),
  new PacketParser('Reserved', 1, { unsigned: true, visible: false })
]

const totalLength = parsers.reduce((sum, parser) => sum + parser.length, 0)

console.log(totalLength)
export default {
  props: {
    identityNo: { type: String, required: true }
  },
  created () {
    this.fetch().finally(() => {
      this.loading = false
      this.interval = setInterval(this.fetch, 1000 * 60)
    })
  },
  destroyed () {
    clearTimeout(this.interval)
  },
  data () {
    return {
      interval: null,
      item: null,
      loading: true,
      selected: -1
    }
  },
  computed: {
    hexChunks () {
      if (!this.item) return []

      let offset = 0
      return parsers.map(({ length }) => {
        const r = this.item.packet.data.slice(offset, offset += length).map(int => int.toString(16).padStart(2, '0').toUpperCase()).join(' ')

        return r
      })
    },
    parsed () {
      if (!this.item) return null

      const view = new DataView(new ArrayBuffer(128))

      this.item.packet.data.forEach((byte, index) => {
        view.setInt8(index, byte)
      })

      console.log(view)

      let offset = 0

      const r = parsers.map((parser) => {
        const value = parser.method(offset)(view)
        offset += parser.length

        return {
          label: parser.label,
          value,
          filter: parser.filter,
          visible: parser.visible,
          suffix: parser.suffix
        }
      })

      return r
    }
  },
  methods: {
    async fetch () {
      const { item } = await getLastPacket({ identityNo: this.identityNo })

      console.log(item)

      this.item = item
    },
    select (index) {
      if (this.selected === index) this.selected = -1
      else this.selected = index
    },
    selectedClass (index, selected) {
      if (selected < 0) return null
      return index === selected ? 'selected' : 'none'
    }
  }
}
</script>

<style scoped>
.hexblock-1 {
  background-color: #FFCDD2
}

.hexblock-2 {
  background-color: #E1BEE7
}

.hexblock-3 {
  background-color: #BBDEFB
}

.hexblock-4 {
  background-color: #B2DFDB
}

.hexblock-5 {
  background-color: #F0F4C3
}

.hexblock-6 {
  background-color: #FFE0B2
}

.none {
  background-color: #FFFFFF;
}
</style>
